import type { PointerEvent } from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { HEIGHT_SCALE_MINIMIZER, INITIAL_KONVA_ZOOM_SCALE, KONVA_ZOOM_SCALE } from '../constants'
import { assert } from '../guards'
import type { Nullable, Point2D, Range2D, Size } from '../types'
import { convertGlobalGeologicalMeasureToLocal } from '../utils'

const getValidPosition = (position: Point2D) => {
  return { x: !isNaN(position.x) ? position.x : 0, y: !isNaN(position.y) ? position.y : 0 }
}

export const useZoom = (
  { height: sceneHeight, width: sceneWidth }: Size,
  origin: Range2D,
  maxZoom?: number,
  minZoom?: number,
) => {
  const [stageScale, setStageScale] = useState<Nullable<number>>(null)
  const [stagePosition, setStagePosition] = useState({ x: 0, y: 0 })
  const [isPointerCapture, setIsPointerCapture] = useState<boolean>(false)
  const [pointerCapturePosition, setPointerCapturePosition] = useState({ x: 0, y: 0 })
  const [startStagePosition, setStartStagePosition] = useState({ x: 0, y: 0 })

  const scale = useMemo(() => stageScale ?? INITIAL_KONVA_ZOOM_SCALE, [stageScale])

  const handleStageWheel = useCallback(
    (
      deltaY: number,
      oldScale?: number,
      stageX?: number,
      stageY?: number,
      pointer?: Nullable<Point2D>,
    ) => {
      if (oldScale === undefined || stageX === undefined || stageY === undefined) return

      assert(pointer != null)

      const direction = Math.sign(deltaY)

      const stageMousePointTo = {
        x: (pointer.x - stageX) / oldScale,
        y: (pointer.y - stageY) / oldScale,
      }

      let newScale = direction <= 0 ? scale * KONVA_ZOOM_SCALE : scale / KONVA_ZOOM_SCALE

      if (maxZoom !== undefined && newScale > maxZoom) newScale = maxZoom

      if (minZoom !== undefined && newScale < minZoom) newScale = minZoom

      setStageScale(newScale)

      setStagePosition({
        x: pointer.x - stageMousePointTo.x * newScale,
        y: pointer.y - stageMousePointTo.y * newScale,
      })
    },
    [scale, maxZoom, minZoom],
  )

  const fitImageWithSize = useCallback(
    (sizes: Size) => {
      const { width: bmWidth, height: bmHeight } = sizes

      const targetScaleWidth = sceneWidth / bmWidth
      const targetScaleHeight = sceneHeight / bmHeight
      const verticalPictureRatio =
        sceneWidth < sceneHeight ? sceneHeight / sceneWidth : sceneWidth / sceneHeight

      // Т.к. обозначения координат на осях находятся ниже и выше значений самой сетки, необходимо уменьшить начальную величину scale
      const newScale =
        Math.min(targetScaleHeight * HEIGHT_SCALE_MINIMIZER, targetScaleWidth) /
        verticalPictureRatio
      const position = {
        x: (sceneWidth - bmWidth * newScale) / 2,
        y: (sceneHeight - bmHeight * newScale) / 2,
      }

      setStageScale(newScale)
      setStagePosition(position)
    },
    [sceneHeight, sceneWidth],
  )

  const fitImageWithOrigin = useCallback(
    (originSize: Range2D) => {
      fitImageWithSize(convertGlobalGeologicalMeasureToLocal(originSize, originSize))
    },
    [fitImageWithSize],
  )

  const handleDivPointerMove = useCallback(
    (event: PointerEvent<HTMLDivElement>, isLayerExist: boolean, pointer?: Nullable<Point2D>) => {
      if (!isPointerCapture || !pointer || !isLayerExist) return

      const offsetX = pointer.x - startStagePosition.x
      const offsetY = pointer.y - startStagePosition.y

      setStagePosition({
        x: pointerCapturePosition.x + offsetX,
        y: pointerCapturePosition.y + offsetY,
      })
    },
    [
      isPointerCapture,
      scale,
      startStagePosition.x,
      startStagePosition.y,
      pointerCapturePosition.x,
      pointerCapturePosition.y,
    ],
  )

  const handleDivPointerDown = useCallback(
    (pointer?: Nullable<Point2D>) => {
      assert(pointer != null)

      setIsPointerCapture(true)
      setStartStagePosition(pointer)
      setPointerCapturePosition(stagePosition)
    },
    [stagePosition],
  )

  const handleDivPointerUp = useCallback(() => {
    setIsPointerCapture(false)
  }, [])

  useEffect(() => {
    fitImageWithOrigin(origin)
  }, [fitImageWithOrigin, origin.height, origin.width])

  useEffect(() => {
    document.addEventListener('pointerup', handleDivPointerUp)

    return () => {
      document.removeEventListener('pointerup', handleDivPointerUp)
    }
  }, [handleDivPointerUp])

  return {
    scale,
    position: getValidPosition(stagePosition),
    handleStageWheel,
    handleDivPointerMove,
    handleDivPointerDown,
    handleDivPointerUp,
    fitImageWithOrigin,
  }
}
